import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { badgeClass } from './tableBadgeStyles';
import {
  getActiveTableUiThemeId,
  isRefinedTableUi,
  subscribeTableUiTheme,
} from './tableUiThemeRuntime';

/** Soft semantic palette (aligned with refined table badges). */
const REFINED = {
  primary:
    'bg-blue-100/80 text-blue-800 border-blue-200/80 dark:bg-blue-950/35 dark:text-blue-200 dark:border-blue-800/45',
  success:
    'bg-emerald-100/80 text-emerald-800 border-emerald-200/80 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-800/45',
  warning:
    'bg-amber-100/80 text-amber-800 border-amber-200/80 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800/45',
  danger:
    'bg-rose-100/80 text-rose-800 border-rose-200/80 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-800/45',
  info: 'bg-sky-100/80 text-sky-800 border-sky-200/80 dark:bg-sky-950/30 dark:text-sky-200 dark:border-sky-800/45',
  secondary:
    'bg-slate-100/80 text-slate-700 border-slate-300/70 dark:bg-slate-800/45 dark:text-slate-200 dark:border-slate-500/45',
  neutral:
    'bg-slate-100/80 text-slate-600 border-slate-300/70 dark:bg-slate-800/45 dark:text-slate-300 dark:border-slate-500/45',
};

const REFINED_TEXT = {
  primary: 'text-blue-700 dark:text-blue-300',
  success: 'text-emerald-700 dark:text-emerald-300',
  warning: 'text-amber-700 dark:text-amber-300',
  danger: 'text-rose-700 dark:text-rose-300',
  info: 'text-sky-700 dark:text-sky-300',
  secondary: 'text-slate-600 dark:text-slate-300',
  neutral: 'text-slate-600 dark:text-slate-400',
};

const REFINED_ICON_BG = {
  primary: 'bg-blue-100/90 dark:bg-blue-950/40',
  success: 'bg-emerald-100/90 dark:bg-emerald-950/35',
  warning: 'bg-amber-100/90 dark:bg-amber-950/35',
  danger: 'bg-rose-100/90 dark:bg-rose-950/35',
  info: 'bg-sky-100/90 dark:bg-sky-950/35',
  secondary: 'bg-slate-100/90 dark:bg-slate-800/45',
  neutral: 'bg-slate-100/90 dark:bg-slate-800/45',
};

const REFINED_FILL = {
  primary: 'bg-blue-500/75 dark:bg-blue-500/60',
  success: 'bg-emerald-500/75 dark:bg-emerald-500/60',
  warning: 'bg-amber-500/75 dark:bg-amber-500/60',
  danger: 'bg-rose-500/75 dark:bg-rose-500/60',
  info: 'bg-sky-500/75 dark:bg-sky-500/60',
  secondary: 'bg-slate-400/75 dark:bg-slate-500/60',
  neutral: 'bg-slate-400/60',
};

const REFINED_DOT = {
  primary: 'bg-blue-500 dark:bg-blue-400',
  success: 'bg-emerald-500 dark:bg-emerald-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  danger: 'bg-rose-500 dark:bg-rose-400',
  info: 'bg-sky-500 dark:bg-sky-400',
  secondary: 'bg-slate-400',
  neutral: 'bg-slate-400',
};

const SEMANTIC_KEYS = ['primary', 'success', 'warning', 'danger', 'info', 'secondary', 'neutral'];

function pickSemantic(theme, key) {
  if (SEMANTIC_KEYS.includes(key) && theme?.colors?.[key]) {
    return theme.colors[key];
  }
  return theme?.colors?.info || 'bg-blue-600';
}

/**
 * Dashboard styling helpers — follows table UI theme (legacy = solid theme colors, refined = soft pastels).
 * @param {object} theme — ThemeContext theme
 * @param {boolean} [isRefined] — override; default from tableUiTheme runtime
 */
export function createDashboardUi(theme, isRefined = isRefinedTableUi()) {
  const bgToText = (bg) => (bg || '').replace(/bg-/g, 'text-');

  return {
    isRefined,

    /** Theme color class (legacy) or refined token — for metric.color assignments */
    color(key = 'primary') {
      return isRefined ? REFINED[key] || REFINED.info : pickSemantic(theme, key);
    },

    fill(key = 'primary') {
      return isRefined ? REFINED_FILL[key] || REFINED_FILL.info : pickSemantic(theme, key);
    },

    dot(key = 'primary') {
      return isRefined ? REFINED_DOT[key] || REFINED_DOT.info : pickSemantic(theme, key);
    },

    text(key = 'primary') {
      return isRefined ? REFINED_TEXT[key] || REFINED_TEXT.info : bgToText(pickSemantic(theme, key));
    },

    iconBg(key = 'primary') {
      if (isRefined) {
        return cn('rounded-md', REFINED_ICON_BG[key] || REFINED_ICON_BG.info);
      }
      return cn(pickSemantic(theme, key), 'bg-opacity-10 rounded-md');
    },

    iconText(key = 'primary') {
      return this.text(key);
    },

    /** Status / count chip */
    pill(key = 'primary', extra = '') {
      if (isRefined) {
        return cn(
          'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium border',
          REFINED[key] || REFINED.info,
          extra
        );
      }
      return cn(
        'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
        pickSemantic(theme, key),
        theme.text.inverse,
        '[&_span]:text-inherit [&_svg]:text-inherit',
        extra
      );
    },

    /** Small badge (table-like) */
    badge(key = 'primary', extra = '') {
      if (isRefined) {
        return cn(badgeClass(REFINED[key] || REFINED.info), extra);
      }
      return cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        pickSemantic(theme, key),
        theme.text.inverse,
        extra
      );
    },

    btnPrimary(extra = '') {
      if (isRefined) {
        return cn(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium',
          'bg-slate-700 text-white hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500',
          'transition-colors',
          extra
        );
      }
      return cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity',
        pickSemantic(theme, 'primary'),
        theme.text.inverse,
        extra
      );
    },

    metricCard(extra = '') {
      if (isRefined) {
        return cn(
          'relative rounded-lg border border-slate-200/80 dark:border-slate-600/50',
          'bg-white/90 dark:bg-slate-800/35 p-3 transition-all duration-200',
          'hover:border-slate-300 dark:hover:border-slate-500 hover:shadow-sm',
          extra
        );
      }
      return cn(
        'relative rounded-lg border p-3 transition-all duration-200 group',
        theme.gradients.card,
        theme.border.light,
        `hover:${theme.shadow.default}`,
        extra
      );
    },

    statCard(extra = '') {
      if (isRefined) {
        return cn(
          'rounded-lg border border-slate-200/80 dark:border-slate-600/50',
          'bg-slate-50/60 dark:bg-slate-800/30 p-3 text-center',
          extra
        );
      }
      return cn(
        'rounded-lg border p-3 text-center',
        theme.gradients.card,
        theme.border.light,
        extra
      );
    },

    widgetShell(extra = '') {
      return cn(
        theme.colors.surface,
        'rounded-xl border p-6',
        isRefined ? 'border-slate-200/80 dark:border-slate-600/50 shadow-sm' : cn(theme.shadow.lg, theme.border.light),
        extra
      );
    },

    pageBg(extra = '') {
      return cn(isRefined ? 'bg-slate-50 dark:bg-slate-950' : theme.gradients.background, extra);
    },

    listItem(extra = '') {
      if (isRefined) {
        return cn(
          'rounded-lg border border-slate-200/60 dark:border-slate-600/40',
          'bg-slate-50/50 dark:bg-slate-800/25 p-3 transition-colors hover:bg-slate-100/80 dark:hover:bg-slate-800/50',
          extra
        );
      }
      return cn(
        theme.gradients.subtle,
        `hover:${theme.colors.background}`,
        'transition-colors duration-200 rounded-lg p-3',
        extra
      );
    },

    progressTrack: 'w-full bg-slate-200/90 dark:bg-slate-700/80 rounded-full',

    progressBar(key = 'success') {
      return cn('h-full rounded-full transition-all duration-500', this.fill(key));
    },

    /** SVG ring / chart stroke (refined uses Tailwind stroke-* classes) */
    ringStroke(score) {
      if (isRefined) {
        if (score >= 90) return 'stroke-emerald-500';
        if (score >= 70) return 'stroke-amber-500';
        return 'stroke-rose-500';
      }
      return null;
    },

    scoreSemantic(score) {
      if (score >= 90) return 'success';
      if (score >= 70) return 'warning';
      return 'danger';
    },

    fillForStatus(statusName) {
      return this.fill(statusToSemantic(statusName));
    },

    dotForStatus(statusName) {
      return this.dot(statusToSemantic(statusName));
    },

    /** Contract-style status label → badge classes */
    statusBadge(statusName) {
      const s = (statusName || '').toLowerCase();
      if (s.includes('active') && !s.includes('expir')) return this.badge('success');
      if (s.includes('expir') && s.includes('soon')) return this.badge('warning');
      if (s.includes('expir')) return this.badge('danger');
      if (s.includes('renew')) return this.badge('info');
      if (s.includes('open') || s.includes('pending')) return this.badge('warning');
      if (s.includes('progress')) return this.badge('info');
      if (s.includes('closed') || s.includes('resolved') || s.includes('approved')) return this.badge('success');
      if (s.includes('reject') || s.includes('overdue') || s.includes('critical') || s.includes('high')) {
        return this.badge('danger');
      }
      if (s.includes('medium')) return this.badge('warning');
      if (s.includes('low')) return this.badge('success');
      return this.badge('neutral');
    },

    linkChip(extra = '') {
      if (isRefined) {
        return cn(
          'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
          'bg-blue-100/80 text-blue-800 border-blue-200/80',
          'hover:bg-blue-200/80 dark:bg-blue-950/35 dark:text-blue-200 dark:border-blue-800/45 dark:hover:bg-blue-900/50',
          extra
        );
      }
      return cn(
        'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
        'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800',
        extra
      );
    },
  };
}

/** React hook — re-renders when Κλασικό / Νέο table UI theme changes. */
export function useDashboardUi(theme) {
  const [uiThemeId, setUiThemeId] = useState(getActiveTableUiThemeId);

  useEffect(() => subscribeTableUiTheme(setUiThemeId), []);

  return createDashboardUi(theme, uiThemeId === 'refined');
}

/** Map free-text status labels to semantic palette keys. */
export function statusToSemantic(statusName) {
  const s = String(statusName?.name || statusName || '').toLowerCase();

  if (s.includes('active') && !s.includes('inactive') && !s.includes('expir')) return 'success';
  if (s.includes('expir') && s.includes('soon')) return 'warning';
  if (s.includes('expir')) return 'danger';
  if (s.includes('renew')) return 'info';
  if (s.includes('open') || s.includes('pending') || s.includes('assigned')) return 'warning';
  if (s.includes('progress') || s.includes('investigation') || s.includes('review')) return 'info';
  if (
    s.includes('closed') ||
    s.includes('resolved') ||
    s.includes('approved') ||
    s.includes('completed') ||
    s.includes('publish')
  ) {
    return 'success';
  }
  if (s.includes('reject') || s.includes('overdue') || s.includes('failed') || s.includes('critical')) {
    return 'danger';
  }
  if (s.includes('high') || s.includes('major')) return 'danger';
  if (s.includes('medium') || s.includes('average')) return 'warning';
  if (s.includes('low') || s.includes('minor') || s.includes('observation')) return 'success';
  if (s.includes('draft')) return 'info';
  if (s.includes('inactive') || s.includes('suspended')) return 'secondary';
  if (s.includes('verification') || s.includes('corrective')) return 'info';
  if (s.includes('document')) return 'primary';
  if (s.includes('complaint')) return 'warning';
  if (s.includes('deviation') || s.includes('risk')) return 'danger';
  if (s.includes('approval')) return 'info';
  return 'neutral';
}

export default createDashboardUi;
